import React from 'react';
import analyticsService from 'src/helpers/analytics.service';
import { useNavigate, useLocation } from 'react-router-dom';
import { EVENTS, ROUTES, INTERNAL_ROUTING } from 'shared/constants';
import styles from './search.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { IconButton } from 'src/components/design-system/buttons/icon-button';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { nanoid } from 'nanoid';
import { resetSearchTracking } from 'src/pages/application/search/Search.page.component';

const SearchComponent = ({ updateSearch = () => {}, lang }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const ref = React.useRef();
  const rootRef = React.useRef();

  // Read term from URL if on search page, otherwise empty
  const termFromUrl = React.useMemo(() => {
    if (location.pathname === ROUTES.SEARCH) {
      const params = new URLSearchParams(location.search);
      return params.get(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TERM) || '';
    }
    return '';
  }, [location.pathname, location.search]);

  // Use URL term as source of truth - URL is the single source of truth
  const displayTerm = termFromUrl;

  const [inputValue, setInputValue] = React.useState(displayTerm);
  const [isFocused, setIsFocused] = React.useState(false);

  const onInputChange = React.useCallback(({ target }) => {
    setInputValue(target.value);
  }, []);

  const onClick = React.useCallback(() => {
    if (!isFocused) {
      analyticsService.track(EVENTS.SEARCH.FOCUS);
      setIsFocused(true);
    }
  }, [isFocused]);

  const onBlur = React.useCallback(
    e => {
      // Check if focus is moving to another element within the search component
      const relatedTarget = e.relatedTarget;
      if (rootRef.current?.contains(relatedTarget)) {
        return;
      }

      setIsFocused(false);
      // Only clear search if not on the search results page
      if (location.pathname !== ROUTES.SEARCH) {
        setInputValue('');
        updateSearch('');
      }
    },
    [location.pathname, updateSearch],
  );

  const handleSearch = React.useCallback(() => {
    // Don't search if input is empty or only whitespace
    if (!inputValue.trim()) {
      return;
    }

    const submitState = { submitId: nanoid() };

    // Reset context when navigating to search from outside - ensures new contextId and event tracking
    if (location.pathname !== ROUTES.SEARCH) {
      searchContextIdManager.resetContext();
      resetSearchTracking();
      navigate(
        `${ROUTES.SEARCH}?${INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TERM}=${encodeURIComponent(
          inputValue,
        )}`,
        { state: submitState },
      );
    } else {
      navigate(
        `${ROUTES.SEARCH}?${INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TERM}=${encodeURIComponent(
          inputValue,
        )}`,
        { replace: true, state: submitState },
      );
    }
    updateSearch(inputValue);
  }, [updateSearch, inputValue, navigate, location.pathname]);

  const onKeyDown = React.useCallback(
    event => {
      if (event.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  // Sync input with display term (Redux term on search page, URL term elsewhere)
  // This keeps input filled when viewing categories, but clears when leaving search page
  React.useEffect(() => {
    setInputValue(displayTerm);
  }, [displayTerm]);

  // Focus search bar when location state indicates it should be focused
  React.useEffect(() => {
    if (location.state?.focusSearch) {
      ref.current?.focus();
      // Clear the state to prevent re-focusing on subsequent renders
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);

  const clearSearch = React.useCallback(() => {
    // If on search page, just clear the input locally (keeps results visible)
    if (location.pathname === ROUTES.SEARCH) {
      setInputValue('');
      ref.current?.blur();
    } else {
      // If not on search page, clear both input and search state
      setInputValue('');
      updateSearch('');
      ref.current?.focus();
    }
  }, [location.pathname, updateSearch]);

  return (
    <div
      ref={rootRef}
      className={classNames([
        'new-design-system',
        styles.root,
        { [styles.hasInput]: inputValue.trim() },
      ])}
    >
      <input
        type="search"
        ref={ref}
        onClick={onClick}
        onBlur={onBlur}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        value={inputValue}
        className={styles.input}
        placeholder={lang.SEARCH_PLACEHOLDER}
      />
      {inputValue.trim() && (
        <button
          type="button"
          aria-label={lang.CLEAR_SEARCH}
          className={styles.clearSearch}
          onMouseDown={e => e.preventDefault()}
          onClick={clearSearch}
        >
          <FontAwesomeIcon className={styles.clearSearchIcon} icon={faXmark} />
        </button>
      )}
      <IconButton
        label={inputValue.trim() ? lang.SEARCH_PLACEHOLDER : lang.SEARCH_TERM_MISSING}
        className={styles.searchButton}
        disabled={!inputValue.trim()}
        onMouseDown={e => e.preventDefault()}
        onClick={handleSearch}
        showTooltip={false}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </IconButton>
    </div>
  );
};

export { SearchComponent };
